import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  FormAnswerRestriction,
  FormAnswerRestrictionLabels,
  FormAnswerRestrictionOption,
} from '../../models/TaskForm/TaskForm';
import { initialState, FORM_TYPES, TaskFormsResponse } from './TaskFormState';
import {
  getTaskForms,
  getTaskFormTemplates,
  getTaskFormManagementTemplates,
  getTaskFormBatches,
  createFormTemplate,
  deleteFormTemplate,
} from './TaskFormRequests';
import {
  getTaskFormTypes,
  getTaskFormAnswerRestrictions,
  getUsersByIds,
} from './TaskFormDictionaries';
import { uploadTaskForms } from './TaskFormFiles';
export type {
  TaskFormsResponse,
  TaskFormsRequest,
  TaskFormTemplatesRequest,
  TaskFormsReportRequest,
  UploadResult,
  UploadTasksPayload,
  FormTypeLabels,
  FormTypesPayload,
} from './TaskFormState';
export {
  getTaskForms,
  getTaskFormTemplates,
  getTaskFormManagementTemplates,
  getTaskFormBatches,
  createFormTemplate,
  deleteFormTemplate,
} from './TaskFormRequests';
export {
  getTaskFormTypes,
  getTaskFormAnswerRestrictions,
  getUsersByIds,
} from './TaskFormDictionaries';
export {
  getTaskFormReport,
  uploadTaskForms,
  downloadTaskFormTemplate,
} from './TaskFormFiles';

const FORM_ANSWER_RESTRICTIONS = Object.values(FormAnswerRestriction);
const isFormAnswerRestriction = (
  value: unknown,
): value is FormAnswerRestriction =>
  FORM_ANSWER_RESTRICTIONS.some((restriction) => restriction === value);

export const taskFormSlice = createSlice({
  name: 'taskForm',
  initialState,
  reducers: {
    clearUploadResult(state) {
      state.uploadResult = null;
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getTaskForms.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(
        getTaskForms.fulfilled,
        (state, action: PayloadAction<TaskFormsResponse>) => {
          state.status.responseLoading = false;
          state.items = action.payload.items ?? [];
          state.totalItemsCount = action.payload.totalItemsCount ?? 0;
        },
      )
      .addCase(getTaskForms.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError =
          'Не удалось загрузить задания анкетирования';
      })

      .addCase(getTaskFormTypes.fulfilled, (state, action) => {
        const { types, labels } = action.payload;
        state.types = types.length > 0 ? types : FORM_TYPES;
        state.typeLabels = labels;
      })

      .addCase(getTaskFormTemplates.pending, (state) => {
        state.templatesStatus.responseLoading = true;
        state.templatesStatus.responseError = null;
      })
      .addCase(getTaskFormTemplates.fulfilled, (state, action) => {
        state.templatesStatus.responseLoading = false;
        state.templates = action.payload.items ?? [];
        state.templatesTotalItemsCount = action.payload.totalItemsCount ?? 0;
      })
      .addCase(getTaskFormTemplates.rejected, (state) => {
        state.templatesStatus.responseLoading = false;
        state.templatesStatus.responseError =
          'Не удалось загрузить шаблоны анкет';
      })

      .addCase(getTaskFormManagementTemplates.pending, (state, action) => {
        state.managementTemplatesStatus.responseLoading = true;
        state.managementTemplatesStatus.responseError = null;
        state.managementTemplatesRequestId = action.meta.requestId;
      })
      .addCase(getTaskFormManagementTemplates.fulfilled, (state, action) => {
        if (state.managementTemplatesRequestId !== action.meta.requestId)
          return;

        state.managementTemplatesStatus.responseLoading = false;
        state.managementTemplatesRequestId = null;
        state.managementTemplates = action.payload.items ?? [];
        state.managementTemplatesTotalItemsCount =
          action.payload.totalItemsCount ?? 0;
      })
      .addCase(getTaskFormManagementTemplates.rejected, (state, action) => {
        if (state.managementTemplatesRequestId !== action.meta.requestId)
          return;

        state.managementTemplatesStatus.responseLoading = false;
        state.managementTemplatesStatus.responseError =
          'Не удалось загрузить шаблоны анкет';
        state.managementTemplatesRequestId = null;
      })

      .addCase(getTaskFormAnswerRestrictions.pending, (state) => {
        state.answerRestrictionsStatus.responseLoading = true;
        state.answerRestrictionsStatus.responseError = null;
      })
      .addCase(getTaskFormAnswerRestrictions.fulfilled, (state, action) => {
        state.answerRestrictionsStatus.responseLoading = false;
        state.answerRestrictions = action.payload ?? [];

        const labels: FormAnswerRestrictionLabels = {};
        const options: FormAnswerRestrictionOption[] = [];

        for (const item of state.answerRestrictions) {
          if (!isFormAnswerRestriction(item.key)) continue;

          const label = item.value || item.key;
          labels[item.key] = label;
          options.push({ value: item.key, label });
        }

        state.answerRestrictionLabels = labels;
        state.answerRestrictionOptions = options;
      })
      .addCase(getTaskFormAnswerRestrictions.rejected, (state) => {
        state.answerRestrictionsStatus.responseLoading = false;
        state.answerRestrictionsStatus.responseError =
          'Не удалось загрузить типы ответов анкеты';
      })

      .addCase(getUsersByIds.fulfilled, (state, action) => {
        for (const user of action.payload ?? []) {
          if (!user.key) continue;
          state.userNicknamesById[user.key] = user.value || user.key;
        }
      })

      .addCase(getTaskFormBatches.fulfilled, (state, action) => {
        state.batches = action.payload ?? [];
      })

      .addCase(uploadTaskForms.pending, (state) => {
        state.status.responseLoading = true;
        state.status.requestError = null;
        state.status.requestSuccess = null;
      })
      .addCase(uploadTaskForms.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.uploadResult = action.payload;
        state.status.requestSuccess = 'Задания успешно загружены';
      })
      .addCase(uploadTaskForms.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.requestError = 'Не удалось загрузить задания';
      })

      .addCase(createFormTemplate.pending, (state) => {
        state.status.requestSuccess = null;
        state.status.requestError = null;
      })
      .addCase(createFormTemplate.fulfilled, (state) => {
        state.status.requestSuccess = 'Шаблон анкеты успешно создан';
      })
      .addCase(createFormTemplate.rejected, (state) => {
        state.status.requestError = 'Не удалось создать шаблон анкеты';
      })

      .addCase(deleteFormTemplate.pending, (state) => {
        state.status.requestSuccess = null;
        state.status.requestError = null;
      })
      .addCase(deleteFormTemplate.fulfilled, (state, action) => {
        state.status.requestSuccess = 'Шаблон анкеты успешно удалён';
        state.templates = state.templates.filter(
          (t) => t.id !== action.payload,
        );
        state.managementTemplates = state.managementTemplates.filter(
          (t) => t.id !== action.payload,
        );
      })
      .addCase(deleteFormTemplate.rejected, (state) => {
        state.status.requestError = 'Не удалось удалить шаблон анкеты';
      });
  },
});

export const { clearUploadResult } = taskFormSlice.actions;

export default taskFormSlice.reducer;

export const selectTaskForms = (state: RootState) => state.taskForm.items;
export const selectTaskFormsTotalCount = (state: RootState) =>
  state.taskForm.totalItemsCount;
export const selectTaskFormBatches = (state: RootState) =>
  state.taskForm.batches;
export const selectTaskFormTemplates = (state: RootState) =>
  state.taskForm.templates;
export const selectTaskFormTemplatesTotalCount = (state: RootState) =>
  state.taskForm.templatesTotalItemsCount;
export const selectTaskFormTemplatesStatus = (state: RootState) =>
  state.taskForm.templatesStatus;
export const selectTaskFormManagementTemplates = (state: RootState) =>
  state.taskForm.managementTemplates;
export const selectTaskFormManagementTemplatesTotalCount = (state: RootState) =>
  state.taskForm.managementTemplatesTotalItemsCount;
export const selectTaskFormManagementTemplatesStatus = (state: RootState) =>
  state.taskForm.managementTemplatesStatus;
export const selectTaskFormTypes = (state: RootState) => state.taskForm.types;
export const selectTaskFormTypeLabels = (state: RootState) =>
  state.taskForm.typeLabels;
export const selectTaskFormAnswerRestrictions = (state: RootState) =>
  state.taskForm.answerRestrictions;
export const selectTaskFormAnswerRestrictionLabels = (state: RootState) =>
  state.taskForm.answerRestrictionLabels;
export const selectTaskFormAnswerRestrictionOptions = (state: RootState) =>
  state.taskForm.answerRestrictionOptions;
export const selectTaskFormAnswerRestrictionsStatus = (state: RootState) =>
  state.taskForm.answerRestrictionsStatus;
export const selectTaskFormUserNicknamesById = (state: RootState) =>
  state.taskForm.userNicknamesById;
export const selectTaskFormUploadResult = (state: RootState) =>
  state.taskForm.uploadResult;
export const selectTaskFormStatus = (state: RootState) => state.taskForm.status;
